import type { Service, Stat, TimelineItem, Testimonial } from '@/lib/index';

// ─── SERVICES (grouped by category) ───────────────────────────────────────
export const SERVICE_CATEGORIES = [
  {
    category: 'Operations & Strategy',
    icon: 'Target',
    color: 'bg-blue-50 text-blue-700 border-blue-100',
    services: [
      {
        id: 'operations-strategy',
        title: 'Operations Strategy & Execution',
        description: 'End-to-end operational framework design — KPI/OKR systems, SOPs, business continuity planning, and process improvement cycles that deliver measurable efficiency gains.',
        icon: 'Target',
        category: 'Operations & Strategy',
      },
      {
        id: 'process-improvement',
        title: 'Process Improvement & BPR',
        description: 'Systematic identification and elimination of operational bottlenecks. Business process re-engineering to reduce TAT, improve SLAs, and enhance service delivery across functions.',
        icon: 'Settings',
        category: 'Operations & Strategy',
      },
      {
        id: 'risk-compliance',
        title: 'Risk Management & Compliance',
        description: 'Comprehensive risk framework design with full regulatory compliance (CBN, NIBSS, PCI-DSS, GDPR, AML/CFT). Audit readiness, fraud detection, and zero-tolerance breach management.',
        icon: 'Shield',
        category: 'Operations & Strategy',
      },
      {
        id: 'bidding-negotiation',
        title: 'Bidding & Negotiation Support',
        description: 'Advisory and representation for bidding processes, commercial negotiations, pricing strategy, and partner/client negotiation facilitation.',
        icon: 'FileText',
        category: 'Operations & Strategy',
      },
    ],
  },
  {
    category: 'Fintech & Payments',
    icon: 'CreditCard',
    color: 'bg-violet-50 text-violet-700 border-violet-100',
    services: [
      {
        id: 'payments-fintech',
        title: 'Payments & Fintech Advisory',
        description: 'Deep expertise in NIP, NEFT, NAPS, SWIFT, Card, and POS infrastructure. Advisory on payment system optimisation, settlement, reconciliation, and chargeback management.',
        icon: 'CreditCard',
        category: 'Fintech & Payments',
      },
      {
        id: 'bank-onboarding',
        title: 'Bank Onboarding & PTSP',
        description: 'Support onboarding of banks and financial institutions for PTSP operations, TID migration coordination, and regulatory documentation facilitation.',
        icon: 'Building2',
        category: 'Fintech & Payments',
      },
      {
        id: 'regulatory-oversight',
        title: 'Regulatory Oversight (CBN / NIBSS)',
        description: 'Advisory and monitoring for PTSP and VAS regulatory requirements. Engagement with regulatory bodies, compliance insights, and mitigation recommendations.',
        icon: 'BookOpen',
        category: 'Fintech & Payments',
      },
    ],
  },
  {
    category: 'Marketing & Communications',
    icon: 'Megaphone',
    color: 'bg-amber-50 text-amber-700 border-amber-100',
    services: [
      {
        id: 'digital-marketing',
        title: 'Digital Marketing',
        description: 'Full-spectrum digital marketing — SEO, content marketing, social media management, and performance marketing to grow your brand online.',
        icon: 'TrendingUp',
        category: 'Marketing & Communications',
      },
      {
        id: 'influencer-pr',
        title: 'Influencer Marketing & PR',
        description: 'Strategic influencer campaign design and brand communications. Media relations, press releases, and reputation management to protect and advance brand equity.',
        icon: 'Star',
        category: 'Marketing & Communications',
      },
      {
        id: 'personal-branding',
        title: 'Personal & Executive Branding',
        description: 'Executive personal branding on X (Twitter) and LinkedIn. Thought leadership positioning, content direction, and organic growth advisory for C-suite leaders.',
        icon: 'UserCheck',
        category: 'Marketing & Communications',
      },
      {
        id: 'marketing-events',
        title: 'Marketing Events Advisory',
        description: 'Strategic guidance for product launches, roadshows, trade fairs, and partner engagement events. Calendar planning, budgeting, and post-event evaluation.',
        icon: 'Megaphone',
        category: 'Marketing & Communications',
      },
    ],
  },
  {
    category: 'Business Development',
    icon: 'Handshake',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    services: [
      {
        id: 'merchant-acquisition',
        title: 'Merchant Introduction & Acquisition',
        description: 'Leveraging a 30,000+ merchant network to facilitate acquisition for fintech products across all high-volume commercial locations in Nigeria.',
        icon: 'Handshake',
        category: 'Business Development',
      },
      {
        id: 'vendor-liaison',
        title: 'Vendor Liaison & Partnerships',
        description: 'Vendor identification, evaluation, and partnership facilitation. Aligning vendor capabilities with your business objectives for optimal, cost-effective outcomes.',
        icon: 'Link',
        category: 'Business Development',
      },
      {
        id: 'talent-acquisition',
        title: 'Talent Sourcing & Acquisition',
        description: 'Candidate sourcing for technical, operations, business development, and compliance roles. Advisory on talent suitability, onboarding, and performance frameworks.',
        icon: 'Users',
        category: 'Business Development',
      },
    ],
  },
];

// Flat list for any component that still needs it
export const SERVICES: Service[] = SERVICE_CATEGORIES.flatMap(c => c.services);

export const STATS: Stat[] = [
  { value: '14+', label: 'Years of Experience' },
  { value: '30K+', label: 'Merchant Network' },
  { value: '6', label: 'Geopolitical Zones' },
  { value: '4', label: 'C-Suite / Head Roles' },
];

export const TIMELINE: TimelineItem[] = [
  {
    year: 'Jul 2025 — Present',
    role: 'Chief Operations Officer',
    company: 'ITEX Integrated Services Ltd',
    sector: 'Fintech / Payment Infrastructure, Lagos',
    description: 'Architect and execute enterprise-wide operational strategy across all business units. Provide C-suite leadership over Business Operations, Brand & Communications, Customer Experience, Procurement, Risk, and Compliance. Define KPI/OKR frameworks, spearhead project portfolios, and lead brand & marketing governance.',
    type: 'current',
  },
  {
    year: 'May 2024 — Jun 2025',
    role: 'Chief Operating Officer',
    company: 'Vendy Limited',
    sector: 'Fintech / Digital Payments, Lagos',
    description: 'Directed all operational functions — product development, customer service, and technology. Led payment system optimisation, reduced chargeback resolution cycles, managed vendor payouts and settlements, and drove cross-functional alignment across Product, Sales, Finance, Technology, HR, and Customer Success.',
    type: 'past',
  },
  {
    year: 'Nov 2022 — Apr 2024',
    role: 'Head of Operations',
    company: 'Redtech Limited',
    sector: 'Fintech / Payments, Lagos',
    description: 'Managed end-to-end operations including reconciliation, settlement, revenue assurance, customer fulfilment, and merchant support. Led system integrations and implemented process improvements to optimise payment efficiency and customer experience.',
    type: 'past',
  },
  {
    year: 'Jan 2022 — Oct 2022',
    role: 'Head, Banking Operations',
    company: 'Cashconnect Microfinance Bank',
    sector: 'Microfinance Banking, Lagos',
    description: 'Oversaw all digital channel operations, NIP/Remita settlement accounts, and full loan lifecycle management. Managed chargeback and dispute resolution, produced MIS reports for CEO, and supervised all IT/technology projects for the bank.',
    type: 'past',
  },
  {
    year: 'Jan 2019 — Dec 2021',
    role: 'Settlements & Reconciliation Manager',
    company: 'United Bank for Africa (UBA) Plc',
    sector: 'Tier-1 Commercial Bank, Lagos · NIP / NEFT / NAPS',
    description: 'Managed NIP/NEFT/NAPS settlement processes, performed daily proof of GL accounts, provided NEFT settlement reports for Treasury, and conducted CBN accounts reconciliation with full audit-trail documentation.',
    type: 'past',
  },
  {
    year: 'Mar 2017 — Dec 2018',
    role: 'Funds Transfer Manager — FCY Remittances',
    company: 'United Bank for Africa (UBA) Plc',
    sector: 'Tier-1 Commercial Bank, Lagos',
    description: 'Processed foreign currency transfers in compliance with CBN regulations. Created and managed MT SWIFT messages for international remittances and resolved rate differential issues involving third currencies.',
    type: 'past',
  },
  {
    year: 'May 2013 — Feb 2017',
    role: 'Card & POS Reconciliation and Dispute Officer',
    company: 'United Bank for Africa (UBA) Plc',
    sector: 'Tier-1 Commercial Bank, Lagos',
    description: 'Processed chargeback claims for Visa, Mastercard, Interswitch, and ValueCard. Conducted daily POS transaction reconciliation and maintained relationships with NIBSS, Unified Payments, Interswitch, and partner settlement banks.',
    type: 'past',
  },
  {
    year: 'Jul 2011 — Apr 2013',
    role: 'Customer Service Officer',
    company: 'Skye Bank Plc',
    sector: 'Commercial Banking, Lagos',
    description: 'Delivered frontline customer service, processed account openings, KYC checks, card issuance, and resolution of failed POS/ATM/USSD transactions. Championed e-channel adoption and cross-sold banking products.',
    type: 'past',
  },
];

export const TESTIMONIALS: Testimonial[] = [
  {
    quote: "Ayoola's operational expertise transformed how we manage payments and settlements. His structured approach to reconciliation and compliance gave us confidence during our CBN audit — zero findings.",
    name: 'Chidi Nwachukwu',
    title: 'CEO',
    company: 'FinPay Solutions',
    initials: 'CN',
    category: 'operations',
  },
  {
    quote: "Working with AO Consulting on our PTSP regulatory compliance was seamless. His industry connections and deep understanding of NIBSS and CBN requirements saved us months of back-and-forth.",
    name: 'Adaeze Okonkwo',
    title: 'Head of Partnerships',
    company: 'SwiftPay Technologies',
    initials: 'AO',
    category: 'operations',
  },
  {
    quote: "The digital marketing and influencer strategy AO Consulting developed doubled our brand visibility in three months. Results-driven, professional, and deeply connected in the Nigerian market.",
    name: 'Emeka Balogun',
    title: 'Marketing Director',
    company: 'Zenith Fintech',
    initials: 'EB',
    category: 'marketing',
  },
];

export const DIGITAL_MARKETING_TESTIMONIALS: Testimonial[] = [
  {
    quote: "AO Consulting managed our brand's social media presence and within 60 days our engagement tripled. The content strategy was sharp, targeted, and consistently on-brand. Our DMs have never been busier.",
    name: 'Tobi Adeyemi',
    title: 'Founder',
    company: 'Luminance Skincare',
    initials: 'TA',
    category: 'marketing',
  },
  {
    quote: "We ran an influencer campaign through AO Consulting for our product launch. The reach was extraordinary — over 2 million impressions in the first week alone, with a conversion rate that exceeded all our projections.",
    name: 'Funmi Okafor',
    title: 'Head of Marketing',
    company: 'TechBridge Africa',
    initials: 'FO',
    category: 'marketing',
  },
  {
    quote: "Ayoola handled our CEO's personal brand on LinkedIn and X. In 3 months our CEO went from invisible to being featured in two industry publications. The thought leadership strategy was exceptional.",
    name: 'Seun Afolabi',
    title: 'COO',
    company: 'Apex Fintech Solutions',
    initials: 'SA',
    category: 'marketing',
  },
  {
    quote: "The PR strategy AO Consulting developed for our brand launch got us coverage in BusinessDay, TechCabal, and three major radio slots — all organic. Incredible network and media relationships.",
    name: 'Kemi Lawson',
    title: 'Brand Manager',
    company: 'Glow Energy Nigeria',
    initials: 'KL',
    category: 'marketing',
  },
];

export const INDUSTRY_LOGOS = [
  { name: 'Deposit Money Banks', label: 'Deposit Money Banks' },
  { name: 'Fintech Companies', label: 'Fintech Companies' },
  { name: 'Payment Service Providers', label: 'Payment Service Providers' },
  { name: 'SMEs & Startups', label: 'SMEs & Startups' },
  { name: '30,000+ Merchants', label: '30,000+ Merchants' },
  { name: 'Microfinance Banks', label: 'Microfinance Banks' },
];

export const PARTNERS = [
  {
    name: 'UBA Group PLC',
    // Official UBA colored logo — red wordmark on white
    logo: 'https://cdn.brandfetch.io/idubagroup.com/w/400/h/400/theme/light/icon.jpeg?c=1bx1743948000000',
    fallbackLogo: 'https://upload.wikimedia.org/wikipedia/en/thumb/2/2c/United_Bank_for_Africa_logo.svg/1280px-United_Bank_for_Africa_logo.svg.png',
    hasLogo: true,
    bg: '#FFFFFF',
  },
  {
    name: 'Redtech Limited',
    logo: 'https://redtechlimited.com/wp-content/uploads/2025/10/new6.png',
    hasLogo: true,
    bg: '#FFFFFF',
  },
  {
    name: 'Heirs Insurance',
    logo: 'https://cdn.brandfetch.io/idACnFaBea/w/2147/h/346/theme/light/logo.png?c=1bxid64Mup7aczewSAYMX',
    hasLogo: true,
    bg: '#FFFFFF',
  },
  {
    name: 'Transcorp Power',
    logo: 'https://images.africanfinancials.com/4abd4fae-ng-tpower-logo.png',
    hasLogo: true,
    bg: '#FFFFFF',
  },
  {
    name: 'Providus Bank',
    logo: 'https://cdn.brandfetch.io/idprovidusbank.com/theme/light/logo.svg?c=1bx1743948000000',
    fallbackLogo: 'https://upload.wikimedia.org/wikipedia/en/a/ac/Providus_Bank_logo.png',
    hasLogo: true,
    bg: '#FFFFFF',
  },
  {
    name: 'Gabriel Tosh',
    logo: 'https://salescabal.s3.eu-west-3.amazonaws.com/stores/100758/media/601acfaf163e7984799ac91fed3c51cdf268506d.png',
    hasLogo: true,
    bg: '#FFFFFF',
  },
  {
    name: 'Tony Elumelu Foundation',
    logo: 'https://www.africa-newsroom.com/files/large/cacd96f8d98e856/200/150',
    fallbackLogo: 'https://www.tonyelumelufoundation.org/wp-content/uploads/2021/06/TEF-social-banner.jpg',
    hasLogo: true,
    bg: '#FFFFFF',
  },
  {
    name: 'YBB',
    logo: '',
    hasLogo: false,
    initials: 'YBB',
    color: '#6B21A8',
    bg: '#6B21A8',
  },
  {
    name: 'And Many More',
    logo: '',
    hasLogo: false,
    initials: '···',
    color: '#64748B',
    bg: '#64748B',
  },
];

export const COMPETENCIES = [
  'Operations Strategy & Execution',
  'Payments Processing & Settlement',
  'Risk Management & Compliance',
  'AML / KYC / PCI-DSS / GDPR',
  'Reconciliation & Revenue Assurance',
  'Brand & Corporate Communications',
  'Regulatory Affairs (CBN, NIBSS)',
  'Cross-functional Team Leadership',
  'P&L / Budget Management',
  'Fraud Detection & Prevention',
  'Business Continuity Planning',
  'Customer Experience Management',
];

export const CERTIFICATIONS = [
  { title: 'Google Project Management: Specialization', issuer: 'Google / Coursera' },
  { title: 'Operations and Process Management', issuer: 'Illinois Institute of Technology' },
  { title: 'HRCI Human Resource Associate', issuer: 'Google / Coursera' },
  { title: 'Anti-Money Laundering & CFT (AML/CFT)', issuer: 'Annual Training, UBA Plc' },
];

// ─── IMAGES ────────────────────────────────────────────────────────────────
export const TEAM_MEETINGS_IMAGE = 'https://images.unsplash.com/photo-1739298061707-cefee19941b7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w4NDU2OTd8MHwxfHNlYXJjaHw2fHxOaWdlcmlhJTIwZmludGVjaCUyMGJhbmtpbmclMjBvcGVyYXRpb25zJTIwbWVldGluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzU0NTkzNTd8MA&ixlib=rb-4.1.0&q=80&w=1080';

// Modern Nigerian office interior — clean, professional workspace
export const ABOUT_OFFICE_IMAGE = 'https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w4MTk0NTh8MHwxfHNlYXJjaHw3fHxtb2Rlcm4lMjBOaWdlcmlhbiUyMG9mZmljZSUyMHByb2Zlc3Npb25hbCUyMHdvcmtzcGFjZSUyMGludGVyaW9yfGVufDB8fHx8MTc3NTQ2MzQ5N3ww&ixlib=rb-4.1.0&q=80&w=1080';

export const BUSINESS_TEAM_IMAGE = 'https://images.unsplash.com/photo-1758519288417-d359ac3c494d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w4MTk0NTh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHN0cmF0ZWd5JTIwY29uc3VsdGluZyUyMHRlYW0lMjBvZmZpY2V8ZW58MHx8fHwxNzc1NDU5MzU4fDA&ixlib=rb-4.1.0&q=80&w=1080';
